import { useEffect, useState } from 'react';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { Version, VersionId } from '../../common/model/index.tsx';
import {
	getUnresolvedIssuesCount,
	getVersion as getVersionRequest,
} from '../../common/rest/index.tsx';
import {
	transformUnresolvedIssuesCount,
	transformVersion,
} from '../../common/services/transformers.tsx';
import type { ReleasableVersion } from '../model/index.tsx';
import { releaseVersion as releaseVersionRest } from '../rest/index.tsx';
import { addDefaultReleaseDate, transformReleasableToRestVersion } from './transformers.tsx';
import {
	dismissReleaseAutomationBanner,
	fireReleaseAutomationBannerError,
	getHasDismissedReleaseAutomationBanner,
} from './utils.tsx';

export const getVersion = (baseUrl: string, versionId: VersionId): Promise<ReleasableVersion> =>
	getVersionRequest(baseUrl, versionId).then(transformVersion).then(addDefaultReleaseDate);

export const getUnresolvedIssues = (baseUrl: string, versionId: VersionId): Promise<number> =>
	getUnresolvedIssuesCount(baseUrl, versionId).then(transformUnresolvedIssuesCount);

export const releaseVersion = (baseUrl: string, version: ReleasableVersion): Promise<Version> =>
	releaseVersionRest(baseUrl, transformReleasableToRestVersion(version)).then(transformVersion);

export const useReleaseAutomationBanner = () => {
	const [showBanner, setShowBanner] = useState(false);
	const accountId = useAccountId();

	useEffect(() => {
		if (accountId != null) {
			getHasDismissedReleaseAutomationBanner(accountId)
				.then((hasDismissed) => setShowBanner(!hasDismissed))
				.catch(fireReleaseAutomationBannerError);
		}
	}, [accountId]);

	const dismissBanner = async () => {
		setShowBanner(false);
		if (accountId != null) {
			await dismissReleaseAutomationBanner(accountId).catch(fireReleaseAutomationBannerError);
		}
	};

	return { showBanner, dismissBanner };
};
